import { useState } from 'react';
import {
  Box,
  Container,
  Heading,
  Text,
  VStack,
  HStack,
  useColorMode,
  SimpleGrid,
  Icon,
  Flex,
  List,
  ListItem,
  ListIcon,
  Divider,
  Badge,
  Button,
  Link,
  Stat,
  StatLabel,
  StatNumber,
  StatHelpText,
} from '@chakra-ui/react';
import { ExternalLinkIcon } from '@chakra-ui/icons';
import { Link as RouterLink } from 'react-router-dom';
import { NavbarWithCallToAction } from '../../components/chakra/NavbarWithCallToAction/NavbarWithCallToAction';
import { FooterWithFourColumns } from '../../components/chakra/FooterWithFourColumns/FooterWithFourColumns';
import { getColor } from '../../brandConfig';
import { FaCoins, FaCheckCircle, FaArrowRight, FaShieldAlt, FaVoteYea, FaUsers, FaChartLine, FaLock } from 'react-icons/fa';
import { GiScales } from 'react-icons/gi';
import { GiTwoCoins, GiReceiveMoney } from 'react-icons/gi';
import ListBuildingModal from '../../components/ListBuildingModal';

export const WhatIsB52Token = () => {
  const { colorMode } = useColorMode();
  const [isModalOpen, setIsModalOpen] = useState(false);

  const tokenUses = [
    {
      icon: FaShieldAlt,
      title: 'Network Security',
      description: 'Validators stake B52 tokens to secure the network. The more tokens staked, the more secure the network becomes.',
      color: '#3b82f6'
    },
    {
      icon: FaVoteYea,
      title: 'Governance Rights',
      description: 'Token holders can participate in network governance decisions, voting on protocol upgrades and parameter changes.',
      color: '#64ffda'
    },
    {
      icon: GiReceiveMoney,
      title: 'Validator Rewards',
      description: 'Validators and their delegators earn rewards from game rake distributions and network fees.',
      color: '#3b82f6'
    },
    {
      icon: FaLock,
      title: 'Slashing Mechanism',
      description: 'Validators who act maliciously or fail to maintain uptime have their staked tokens slashed, ensuring good behavior.',
      color: '#64ffda'
    }
  ];

  const stakingBenefits = [
    'Earn passive income from validator rewards',
    'Participate in network governance decisions',
    'Support network security and decentralization',
    'Gain exposure to network growth and adoption',
    'No technical knowledge required for delegation',
    'Liquid staking options for flexibility'
  ];

  return (
    <>
      <NavbarWithCallToAction />
      <Box minH="100vh" bg={getColor('background.main', colorMode)} py={20}>
        <Container maxW="900px">
          <VStack spacing={8} align="start">
            {/* Header */}
            <VStack spacing={4} align="start" w="full">
              <Badge
                px={4}
                py={2}
                bgGradient="linear(to-r, #3b82f6, #64ffda)"
                color="white"
                borderRadius="full"
                fontSize="sm"
                fontWeight="bold"
              >
                LEARN
              </Badge>
              <Heading
                as="h1"
                size="2xl"
                color={colorMode === 'light' ? getColor('text.primary', colorMode) : 'white'}
              >
                What is B52 Token?
              </Heading>
              <Text fontSize="xl" color={getColor('text.secondary', colorMode)} lineHeight="tall">
                B52 is the native utility token of the Block52 blockchain. It serves as the economic backbone
                of the network, used for staking, governance, and securing the decentralized poker ecosystem.
              </Text>
            </VStack>

            <Divider />

            {/* Token Stats */}
            <SimpleGrid columns={{ base: 1, md: 3 }} spacing={6} w="full">
              <Box
                p={6}
                bg={colorMode === 'dark' ? 'rgba(59, 130, 246, 0.1)' : 'blue.50'}
                borderRadius="lg"
                border="2px solid"
                borderColor="#3b82f6"
              >
                <Stat>
                  <StatLabel color={getColor('text.secondary', colorMode)}>Total Supply</StatLabel>
                  <StatNumber color={colorMode === 'light' ? getColor('text.primary', colorMode) : 'white'}>
                    52 Million
                  </StatNumber>
                  <StatHelpText color={getColor('text.secondary', colorMode)}>Fixed forever</StatHelpText>
                </Stat>
              </Box>
              <Box
                p={6}
                bg={colorMode === 'dark' ? 'rgba(100, 255, 218, 0.1)' : 'teal.50'}
                borderRadius="lg"
                border="2px solid"
                borderColor="#64ffda"
              >
                <Stat>
                  <StatLabel color={getColor('text.secondary', colorMode)}>Token Type</StatLabel>
                  <StatNumber color={colorMode === 'light' ? getColor('text.primary', colorMode) : 'white'}>
                    Utility
                  </StatNumber>
                  <StatHelpText color={getColor('text.secondary', colorMode)}>Cosmos-based</StatHelpText>
                </Stat>
              </Box>
              <Box
                p={6}
                bg={colorMode === 'dark' ? 'rgba(59, 130, 246, 0.1)' : 'blue.50'}
                borderRadius="lg"
                border="2px solid"
                borderColor="#3b82f6"
              >
                <Stat>
                  <StatLabel color={getColor('text.secondary', colorMode)}>Network</StatLabel>
                  <StatNumber color={colorMode === 'light' ? getColor('text.primary', colorMode) : 'white'}>
                    Block52
                  </StatNumber>
                  <StatHelpText color={getColor('text.secondary', colorMode)}>
                    <Link href="https://app.block52.xyz/explorer/distribution" isExternal color="#3b82f6">
                      View Distribution <ExternalLinkIcon mx="2px" />
                    </Link>
                  </StatHelpText>
                </Stat>
              </Box>
            </SimpleGrid>

            {/* Purpose & Vision */}
            <Box w="full">
              <Heading as="h2" size="lg" mb={4} color={colorMode === 'light' ? getColor('text.primary', colorMode) : 'white'}>
                Purpose & Vision
              </Heading>
              <Text color={getColor('text.secondary', colorMode)} lineHeight="tall" mb={4}>
                The B52 token exists to ensure the Block52 network remains secure, decentralized, and aligned with
                the interests of its users. People who have an interest in the network's success—whether they're club
                owners, players, developers, or investors—can stake B52 tokens to help secure the network.
              </Text>
              <Text color={getColor('text.secondary', colorMode)} lineHeight="tall" mb={4}>
                With only 52 million tokens that will ever exist, B52 has a fixed supply cap. This scarcity,
                combined with growing network usage, creates deflationary pressure as more tokens are locked in
                staking and validator nodes.
              </Text>
              <Box
                p={6}
                bg={colorMode === 'dark' ? 'rgba(59, 130, 246, 0.1)' : 'blue.50'}
                borderRadius="lg"
                borderLeft="4px solid"
                borderColor="#3b82f6"
              >
                <Text color={getColor('text.secondary', colorMode)} fontWeight="medium" fontStyle="italic">
                  "B52 is designed as a utility token that aligns economic incentives—validators earn rewards for
                  securing the network, stakers earn passive income, and the network becomes more secure as adoption grows."
                </Text>
              </Box>
            </Box>

            {/* Token Uses */}
            <Box w="full">
              <Heading as="h2" size="lg" mb={6} color={colorMode === 'light' ? getColor('text.primary', colorMode) : 'white'}>
                How B52 Token is Used
              </Heading>
              <SimpleGrid columns={{ base: 1, md: 2 }} spacing={6}>
                {tokenUses.map((use, index) => (
                  <Box
                    key={index}
                    p={6}
                    borderRadius="lg"
                    bg={colorMode === 'dark' ? 'rgba(31, 41, 55, 0.3)' : 'white'}
                    border="1px solid"
                    borderColor={colorMode === 'dark' ? 'rgba(59, 130, 246, 0.3)' : 'gray.200'}
                    transition="all 0.3s"
                    _hover={{
                      transform: 'translateY(-4px)',
                      borderColor: use.color,
                      boxShadow: `0 8px 16px ${use.color}30`,
                    }}
                  >
                    <Flex align="start" mb={3}>
                      <Flex
                        w="48px"
                        h="48px"
                        borderRadius="lg"
                        bg={`${use.color}20`}
                        align="center"
                        justify="center"
                        mr={4}
                        flexShrink={0}
                      >
                        <Icon as={use.icon} color={use.color} boxSize={6} />
                      </Flex>
                      <Heading size="sm" color={colorMode === 'light' ? getColor('text.primary', colorMode) : 'white'}>
                        {use.title}
                      </Heading>
                    </Flex>
                    <Text color={getColor('text.secondary', colorMode)} fontSize="sm">
                      {use.description}
                    </Text>
                  </Box>
                ))}
              </SimpleGrid>
            </Box>

            {/* Running a Validator */}
            <Box w="full">
              <Heading as="h2" size="lg" mb={4} color={colorMode === 'light' ? getColor('text.primary', colorMode) : 'white'}>
                Running a Validator Node
              </Heading>
              <Text color={getColor('text.secondary', colorMode)} lineHeight="tall" mb={4}>
                Validators are the backbone of the Block52 network. They process transactions, validate game state,
                and secure the blockchain. To become a validator, you need to stake a certain number of B52 tokens
                as collateral.
              </Text>
              <VStack spacing={4} align="start">
                <Box
                  p={5}
                  bg={colorMode === 'dark' ? 'rgba(31, 41, 55, 0.3)' : 'white'}
                  borderRadius="lg"
                  border="1px solid"
                  borderColor={colorMode === 'dark' ? 'rgba(59, 130, 246, 0.3)' : 'gray.200'}
                  w="full"
                >
                  <Flex align="start">
                    <Flex
                      w="32px"
                      h="32px"
                      borderRadius="full"
                      bg="rgba(59, 130, 246, 0.2)"
                      align="center"
                      justify="center"
                      mr={4}
                      flexShrink={0}
                    >
                      <Icon as={FaCoins} color="#3b82f6" boxSize={4} />
                    </Flex>
                    <Box>
                      <Heading size="sm" mb={2} color={colorMode === 'light' ? getColor('text.primary', colorMode) : 'white'}>
                        Token Requirements
                      </Heading>
                      <Text color={getColor('text.secondary', colorMode)} fontSize="sm">
                        Running a validator requires staking B52 tokens as collateral. This stake can be slashed if
                        the validator misbehaves or goes offline, ensuring validators maintain high uptime and honesty.
                      </Text>
                    </Box>
                  </Flex>
                </Box>

                <Box
                  p={5}
                  bg={colorMode === 'dark' ? 'rgba(31, 41, 55, 0.3)' : 'white'}
                  borderRadius="lg"
                  border="1px solid"
                  borderColor={colorMode === 'dark' ? 'rgba(100, 255, 218, 0.3)' : 'gray.200'}
                  w="full"
                >
                  <Flex align="start">
                    <Flex
                      w="32px"
                      h="32px"
                      borderRadius="full"
                      bg="rgba(100, 255, 218, 0.2)"
                      align="center"
                      justify="center"
                      mr={4}
                      flexShrink={0}
                    >
                      <Icon as={FaChartLine} color="#64ffda" boxSize={4} />
                    </Flex>
                    <Box>
                      <Heading size="sm" mb={2} color={colorMode === 'light' ? getColor('text.primary', colorMode) : 'white'}>
                        Earning Rewards
                      </Heading>
                      <Text color={getColor('text.secondary', colorMode)} fontSize="sm">
                        Validators earn rewards from network fees and a portion of rake collected from poker games.
                        These rewards are distributed proportionally based on stake weight and uptime performance.
                      </Text>
                    </Box>
                  </Flex>
                </Box>
              </VStack>
            </Box>

            {/* Staking for Non-Validators */}
            <Box w="full">
              <Heading as="h2" size="lg" mb={4} color={colorMode === 'light' ? getColor('text.primary', colorMode) : 'white'}>
                Staking as a Delegator
              </Heading>
              <Text color={getColor('text.secondary', colorMode)} lineHeight="tall" mb={4}>
                Don't want to run your own validator node? No problem. You can stake your B52 tokens on an existing
                validator and earn a share of their rewards. This is called "delegating" and it requires no technical
                knowledge.
              </Text>
              <List spacing={3} mb={4}>
                {stakingBenefits.map((benefit, index) => (
                  <ListItem key={index}>
                    <Flex align="center">
                      <ListIcon as={FaCheckCircle} color="#64ffda" fontSize="xl" />
                      <Text color={getColor('text.secondary', colorMode)}>{benefit}</Text>
                    </Flex>
                  </ListItem>
                ))}
              </List>
              <Box
                p={5}
                bg={colorMode === 'dark' ? 'rgba(100, 255, 218, 0.1)' : 'teal.50'}
                borderRadius="lg"
                border="1px solid"
                borderColor="#64ffda"
              >
                <Flex align="center" mb={2}>
                  <Icon as={GiTwoCoins} color="#64ffda" boxSize={6} mr={3} />
                  <Heading size="sm" color={colorMode === 'light' ? getColor('text.primary', colorMode) : 'white'}>
                    How Delegation Works
                  </Heading>
                </Flex>
                <Text color={getColor('text.secondary', colorMode)} fontSize="sm">
                  When you delegate your tokens to a validator, you're adding to their stake weight, which increases
                  their chances of being selected to validate blocks. In return, you receive a percentage of the
                  validator's earnings (minus their commission). Your tokens remain in your control and can be
                  undelegated at any time (subject to an unbonding period).
                </Text>
              </Box>
            </Box>

            {/* Tokenomics */}
            <Box w="full">
              <Heading as="h2" size="lg" mb={4} color={colorMode === 'light' ? getColor('text.primary', colorMode) : 'white'}>
                Token Economics
              </Heading>
              <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
                <Box
                  p={5}
                  bg={colorMode === 'dark' ? 'rgba(31, 41, 55, 0.3)' : 'white'}
                  borderRadius="lg"
                  border="1px solid"
                  borderColor={colorMode === 'dark' ? 'rgba(59, 130, 246, 0.3)' : 'gray.200'}
                >
                  <Heading size="sm" mb={2} color={colorMode === 'light' ? getColor('text.primary', colorMode) : 'white'}>
                    Fixed Supply
                  </Heading>
                  <Text color={getColor('text.secondary', colorMode)} fontSize="sm">
                    Only 52 million B52 tokens will ever exist. No inflation, no dilution. This creates scarcity
                    as the network grows.
                  </Text>
                </Box>
                <Box
                  p={5}
                  bg={colorMode === 'dark' ? 'rgba(31, 41, 55, 0.3)' : 'white'}
                  borderRadius="lg"
                  border="1px solid"
                  borderColor={colorMode === 'dark' ? 'rgba(100, 255, 218, 0.3)' : 'gray.200'}
                >
                  <Heading size="sm" mb={2} color={colorMode === 'light' ? getColor('text.primary', colorMode) : 'white'}>
                    Slashing Mechanism
                  </Heading>
                  <Text color={getColor('text.secondary', colorMode)} fontSize="sm">
                    Validators who act maliciously or maintain poor uptime have their staked tokens slashed (burned),
                    permanently reducing supply.
                  </Text>
                </Box>
                <Box
                  p={5}
                  bg={colorMode === 'dark' ? 'rgba(31, 41, 55, 0.3)' : 'white'}
                  borderRadius="lg"
                  border="1px solid"
                  borderColor={colorMode === 'dark' ? 'rgba(59, 130, 246, 0.3)' : 'gray.200'}
                >
                  <Heading size="sm" mb={2} color={colorMode === 'light' ? getColor('text.primary', colorMode) : 'white'}>
                    Reward Distribution
                  </Heading>
                  <Text color={getColor('text.secondary', colorMode)} fontSize="sm">
                    A portion of rake from every poker game is distributed to validators and stakers, creating
                    sustainable yield tied to network activity.
                  </Text>
                </Box>
                <Box
                  p={5}
                  bg={colorMode === 'dark' ? 'rgba(31, 41, 55, 0.3)' : 'white'}
                  borderRadius="lg"
                  border="1px solid"
                  borderColor={colorMode === 'dark' ? 'rgba(100, 255, 218, 0.3)' : 'gray.200'}
                >
                  <Heading size="sm" mb={2} color={colorMode === 'light' ? getColor('text.primary', colorMode) : 'white'}>
                    Network Value Accrual
                  </Heading>
                  <Text color={getColor('text.secondary', colorMode)} fontSize="sm">
                    As more poker clubs launch and games are played, network activity increases, driving more value
                    to validators and token holders.
                  </Text>
                </Box>
              </SimpleGrid>
            </Box>

            {/* Distribution */}
            <Box
              w="full"
              p={6}
              bgGradient={colorMode === 'dark'
                ? 'linear(to-r, rgba(59, 130, 246, 0.1), rgba(100, 255, 218, 0.1))'
                : 'linear(to-r, blue.50, teal.50)'}
              borderRadius="xl"
              border="2px solid"
              borderColor={colorMode === 'dark' ? 'rgba(59, 130, 246, 0.3)' : '#3b82f6'}
            >
              <Heading as="h2" size="lg" mb={4} color={colorMode === 'light' ? getColor('text.primary', colorMode) : 'white'}>
                Token Distribution
              </Heading>
              <Text color={getColor('text.secondary', colorMode)} lineHeight="tall" mb={4}>
                The distribution of B52 tokens is designed to balance network security, community growth, and
                long-term sustainability.
              </Text>
              <Button
                as={RouterLink}
                to="/tokenomics"
                variant="outline"
                borderColor="#64ffda"
                color="#64ffda"
                _hover={{
                  bg: `rgba(100, 255, 218, 0.1)`,
                  textDecoration: 'none',
                }}
                leftIcon={<Icon as={GiScales} />}
              >
                View Full Token Economics
              </Button>
            </Box>

            {/* CTA */}
            <Box w="full" textAlign="center" py={6}>
              <Button
                size="lg"
                px={8}
                py={6}
                bgGradient="linear(to-r, #3b82f6, #64ffda)"
                color="white"
                _hover={{
                  bgGradient: 'linear(to-r, #64ffda, #3b82f6)',
                  transform: 'translateY(-2px)',
                  boxShadow: '0 8px 20px rgba(59, 130, 246, 0.4)',
                }}
                rightIcon={<FaArrowRight />}
                borderRadius="full"
                fontWeight="bold"
                onClick={() => setIsModalOpen(true)}
              >
                Start Now
              </Button>
            </Box>
          </VStack>
        </Container>
      </Box>
      <FooterWithFourColumns />
      <ListBuildingModal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} />
    </>
  );
};

export default WhatIsB52Token;
